import api from './api';
import { ApiResponse, unwrapApiResponse } from '../utils/api-response.util';
import { Locale, SortOrder } from './workout.service';

export type MetCompendiumType = 'adult' | 'youth' | 'elderly';
export type MetSourceType = 'system' | 'user' | 'all';
export type MetSortBy = 'id' | 'code' | 'categoryCode' | 'metValue' | 'nameEn';

export interface MetActivity {
  id: number;
  code: string;
  compendiumType: MetCompendiumType;
  categoryCode: number;
  categoryName: string;
  name: string;
  nameEn: string;
  metValue: number;
  youthAgeGroup: number | null;
  youthAgeGroupLabel: string | null;
  isSystem: boolean;
  keywords: string | null; // власні ключові фрази, одна на рядок
  availableTranslations: { [key in Locale]?: boolean };
}

export interface MetActivityEdit {
  id: number;
  code: string;
  compendiumType: MetCompendiumType;
  categoryCode: number;
  categoryName: string;
  nameEn: string;
  nameI18n: { [key in Locale]?: string } | null;
  metValue: number;
  youthAgeGroup: number | null;
}

export interface CreateMetActivityDto {
  code: string;
  compendiumType: MetCompendiumType;
  categoryCode: number;
  categoryName: string;
  nameEn: string;
  nameI18n?: { [key in Locale]?: string };
  metValue: number;
  youthAgeGroup?: number;
}

export interface MetActivityOptions {
  compendiumTypes: { value: MetCompendiumType; label: string }[];
  categories: { code: number; name: string }[];
  youthAgeGroups: { value: number; label: string }[];
  sourceTypes: { value: MetSourceType; label: string }[];
}

export interface GetMetActivitiesParams {
  page?: number;
  limit?: number;
  search?: string;
  sourceType?: MetSourceType;
  compendiumType?: MetCompendiumType;
  categoryCode?: number;
  sortBy?: MetSortBy;
  sortOrder?: SortOrder;
}

export interface PaginatedMetActivitiesResponse {
  data: MetActivity[];
  meta: { total: number; page: number; limit: number };
}

class MetActivityService {
  private readonly BASE_URL = '/lib/met-activities';

  async getOptions(): Promise<MetActivityOptions> {
    const response = await api.get<ApiResponse<MetActivityOptions>>(`${this.BASE_URL}/options`);
    return unwrapApiResponse<MetActivityOptions>(response);
  }

  async getActivities(params: GetMetActivitiesParams): Promise<PaginatedMetActivitiesResponse> {
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });
    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return { data: body.data, meta: body.meta };
    }
    console.error('Unexpected API response for getMetActivities:', body);
    return { data: [], meta: { total: 0, page: 1, limit: params.limit || 20 } };
  }

  async getActivityForEdit(id: number): Promise<MetActivityEdit> {
    const response = await api.get<ApiResponse<MetActivityEdit>>(`${this.BASE_URL}/${id}/edit`);
    return unwrapApiResponse<MetActivityEdit>(response);
  }

  async createActivity(payload: CreateMetActivityDto): Promise<MetActivity> {
    const response = await api.post<ApiResponse<MetActivity>>(`${this.BASE_URL}`, payload);
    return unwrapApiResponse<MetActivity>(response);
  }

  async updateActivity(id: number, payload: Partial<CreateMetActivityDto>): Promise<MetActivity> {
    const response = await api.patch<ApiResponse<MetActivity>>(`${this.BASE_URL}/${id}`, payload);
    return unwrapApiResponse<MetActivity>(response);
  }

  async deleteActivity(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.BASE_URL}/${id}`);
  }
}

export const metActivityService = new MetActivityService();

/** Ключові фрази MET-активності: у БД - текст, одна фраза на рядок. */
export const splitKeywords = (keywords: string | null | undefined): string[] =>
  (keywords ?? '').split('\n').map(k => k.trim()).filter(Boolean);

export interface MetAiSearchExplanation {
  title: string;
  updatedAt: string;
  markdown: string;
}

/** Пояснення пошуку AI по базі MET - текст живе в бекенд-модулі (одне джерело з документацією коду). */
export const getMetAiSearchExplanation = async (): Promise<MetAiSearchExplanation> => {
  const response = await api.get<ApiResponse<MetAiSearchExplanation>>('/lib/met-activities/ai-search-explanation');
  return unwrapApiResponse<MetAiSearchExplanation>(response);
};
